import { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import LockIcon from "@mui/icons-material/Lock";
import CheckIcon from "@mui/icons-material/Check";
import StyledTextField from "../../../kit/TextField";
import { DetailsHeadings } from "../../../theme/commonElements";
import InfoToolTip from "../../../components/MUIComponents/InfoToolTip";
import { useStyles } from "../../../theme/style";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import { useDispatch } from "react-redux";

export default function TeamDialog({
  open,
  onClose,
  onSave,
  team,
  teams,
  editable_columns,
  theme,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [columns, setColumns] = useState([]);
  const classes = useStyles(theme);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      setName(team?.name || "");
      setDescription(team?.description || "");
      setColumns(team?.columns ? [...team.columns] : []);
    }
  }, [team, open]);

  const ownership = useMemo(() => {
    const map = new Map();
    (teams || []).forEach((t) => {
      if (team && t.id === team.id) return;
      t.columns.forEach((c) => map.set(c, t.name));
    });
    return map;
  }, [teams, team]);

  const duplicateName = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;
    return (teams || []).some(
      (t) => t.name.toLowerCase() === trimmed && (!team || t.id !== team.id)
    );
  }, [name, teams, team]);

  const toggle = (col) => {
    if (ownership.has(col)) return;
    setColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  const handleSave = () => {
    if (!name.trim()) {
      dispatch(
        updateAlertInfo({
          message: "Team name is required",
          open: true,
          severity: "error",
        })
      );
      return;
    }
    if (duplicateName) {
      dispatch(
        updateAlertInfo({
          message: "A team with this name already exists",
          open: true,
          severity: "error",
        })
      );
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim(),
      columns: columns || [],
    };

    if (team) {
      return onSave({
        team_id: team.id,
        name: name.trim(),
        description: description.trim(),
        columns: columns || [],
      });
    } else {
      return onSave({
        team_id: null,
        name: name.trim(),
        description: description.trim(),
        columns: columns || [],
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          background: theme.palette.brain.table.tableBackground,
        },
      }}
    >
      <DialogTitle
        sx={{ fontWeight: 600, color: theme.palette.secondary.main2 }}
      >
        {team ? "Edit Team" : "Add Team"}
      </DialogTitle>
      <DialogContent>
        <DetailsHeadings display="flex">
          Team Name <span className={classes.requiredAsterik}>*</span>
          <InfoToolTip theme={theme} title={"Team Name (required)"} />
        </DetailsHeadings>
        <StyledTextField
          fullWidth
          sx={{
            width: "40%",
            marginBottom: "16px",
          }}
          size="small"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="Enter Team Name"
        />

        <DetailsHeadings display="flex">
          Description
          <InfoToolTip theme={theme} title={"Team description (optinal)"} />
        </DetailsHeadings>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={description}
          placeholder="Enter description"
          onChange={(e) => setDescription(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              flex: 1,
              padding: `${theme.size[8.5]} ${theme.size[13]}`,
              fontSize: theme.size[13],
              color: theme.palette.text.secondary,
              color: theme.palette.form.text,
              backgroundColor: theme.palette.form.fieldBackground,
            },
            "& .MuiOutlinedInput-input": {
              height: "100% !important",
            },
            "&.MuiTextField-root": {
              backgroundColor: theme.palette.grey[4],
              width: "100%",
              height: "100%",
              borderRadius: theme.size[4],
              fontFamily: theme.typography["fontFamily"],
              "&:hover": {
                borderColor: "transparent",
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "transparent !important",
            },
          }}
        />

        <Box sx={{ marginTop: "16px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: theme.palette.brain.table.row.text }}
            >
              Column Access
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.brain.table.row.text }}
            >
              {columns.length} selected • columns owned by other teams are
              locked
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              p: 1.5,
              border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
              borderRadius: 1,
              bgcolor: theme.palette.brain.drawer.background,
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {editable_columns.map((col) => {
              const owner = ownership.get(col);
              const isSelected = columns.includes(col);
              const isLocked = !!owner;
              const chip = (
                <Chip
                  key={col}
                  label={col.replace(/_/g, " ")}
                  size="small"
                  icon={
                    isLocked ? (
                      <LockIcon />
                    ) : isSelected ? (
                      <CheckIcon />
                    ) : undefined
                  }
                  onClick={isLocked ? undefined : () => toggle(col)}
                  sx={{
                    fontSize: "0.72rem",
                    cursor: isLocked ? "not-allowed" : "pointer",
                    bgcolor: isLocked
                      ? theme.palette.brain.mis.grey.disabledBackground
                      : isSelected
                      ? theme.palette.brain.yellow.background
                      : theme.palette.brain.drawer.item_background,
                    color: isLocked
                      ? theme.palette.brain.table.header.restrictedText
                      : isSelected
                      ? theme.palette.brain.yellow.text
                      : theme.palette.brain.table.row.text,
                    border: "1px solid",
                    borderColor: isLocked
                      ? "#ccc"
                      : isSelected
                      ? theme.palette.brain.yellow.border
                      : theme.palette.brain.table.row.text,
                    opacity: isLocked ? 0.7 : 1,
                    "&:hover": {
                      bgcolor: isLocked
                        ? theme.palette.brain.mis.grey.disabledBackground
                        : isSelected
                        ? theme.palette.brain.yellow.background
                        : theme.palette.brain.drawer.item_hover_background,
                    },

                    "& .MuiChip-icon": {
                      fontSize: 14,
                      color: isLocked
                        ? theme.palette.brain.table.row.text
                        : theme.palette.brain.yellow.text,
                    },
                  }}
                />
              );
              return isLocked ? (
                <Tooltip key={col} title={`Assigned to ${owner}`} arrow>
                  {chip}
                </Tooltip>
              ) : (
                chip
              );
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: "none",
            borderColor: theme.palette.secondary.main2,
            color: theme.palette.secondary.main2,
            "&:hover": { borderColor: theme.palette.secondary.main2 },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || duplicateName}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            width: "150px",
            background: theme.palette.secondary.main2,
            "&:hover": {
              background: theme.palette.secondary.main2,
            },
            "&.Mui-disabled": {
              bgcolor: theme.palette.brain.table.header.restrictedBackground,
              color: theme.palette.brain.grey.text,
            },
            color: "#fff",
          }}
        >
          {team ? "Save Team" : "Create Team"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
